import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Upload,
  ClipboardPaste,
  Loader2,
  Sparkles,
  X,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ScriptDropZoneProps {
  onScriptLoaded: (scriptText: string, fileName?: string) => void;
  onAnalyze: () => void;
  scriptContent: string | null;
  scriptFileName: string | null;
  isAnalyzing: boolean;
  assetsCount: number;
}

export const ScriptDropZone: React.FC<ScriptDropZoneProps> = ({
  onScriptLoaded,
  onAnalyze,
  scriptContent,
  scriptFileName,
  isAnalyzing,
  assetsCount,
}) => {
  const [pasteText, setPasteText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showPasteArea, setShowPasteArea] = useState(false);
  const { toast } = useToast();

  const handlePDFUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/script/parse', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to parse PDF');
      }

      const data = await response.json();
      onScriptLoaded(data.text, file.name);

      toast({
        title: 'Script loaded',
        description: `Parsed ${data.pages} page(s) from ${file.name}`,
      });
    } catch (error) {
      console.error('PDF upload error:', error);
      toast({
        title: 'Failed to parse PDF',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      handlePDFUpload(file);
    } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
      // Handle plain text files
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        onScriptLoaded(text, file.name);
        toast({
          title: 'Script loaded',
          description: `Loaded ${file.name}`,
        });
      };
      reader.readAsText(file);
    } else {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF or TXT file',
        variant: 'destructive',
      });
    }
  };

  const handleFileInputClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.txt';
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };
    input.click();
  };

  const handlePasteSubmit = () => {
    if (pasteText.trim()) {
      onScriptLoaded(pasteText.trim(), undefined);
      toast({
        title: 'Script loaded',
        description: 'Script text has been loaded',
      });
    }
  };

  const handleClear = () => {
    onScriptLoaded('', undefined);
    setPasteText('');
  };

  // If script is already loaded, show preview
  if (scriptContent) {
    return (
      <div className="space-y-3">
        {/* Loaded Script Info */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <div>
              <p className="text-sm font-medium">
                {scriptFileName || 'Pasted Script'}
              </p>
              <p className="text-xs text-muted-foreground">
                {scriptContent.length.toLocaleString()} characters
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Script Preview */}
        <ScrollArea className="h-[200px] border rounded-lg">
          <div className="p-3 text-xs font-mono whitespace-pre-wrap text-muted-foreground">
            {scriptContent.slice(0, 2000)}
            {scriptContent.length > 2000 && (
              <span className="text-muted-foreground/50">
                {`\n\n... (${scriptContent.length - 2000} more characters)`}
              </span>
            )}
          </div>
        </ScrollArea>

        {/* Assets Status */}
        <div className="flex items-center gap-2 text-sm">
          {assetsCount > 0 ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-muted-foreground">
                {assetsCount} clip{assetsCount !== 1 ? 's' : ''} in project
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <span className="text-muted-foreground">
                No clips in project yet
              </span>
            </>
          )}
        </div>

        {/* Analyze Button */}
        <Button
          className="w-full bg-purple-600 hover:bg-purple-700"
          onClick={onAnalyze}
          disabled={isAnalyzing || assetsCount === 0}
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Matching Footage...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Match Footage to Script
            </>
          )}
        </Button>

        {assetsCount === 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Add clips to your project first to match footage
          </p>
        )}
      </div>
    );
  }

  // Simplified input mode - two buttons
  return (
    <div className="space-y-3">
      {/* Two simple buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={handleFileInputClick}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Script
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => setShowPasteArea(!showPasteArea)}
        >
          <ClipboardPaste className="h-4 w-4 mr-2" />
          Paste Script
        </Button>
      </div>

      {/* Paste area - shown when button clicked */}
      {showPasteArea && (
        <div className="space-y-2 p-3 border rounded-lg bg-muted/20">
          <Textarea
            placeholder="Paste your script text here..."
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            className="min-h-[120px] font-mono text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                handlePasteSubmit();
                setShowPasteArea(false);
              }}
              disabled={!pasteText.trim()}
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Load Script
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowPasteArea(false);
                setPasteText('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
